import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Global } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import IframeApproval, {
  IframeApprovalMessageAction,
} from '@/components/Approval/IframeApprovalModal/IframeApproval';
import ApprovalView from '@/components/Approval/ApprovalView';
import ApprovalCreate from '@/components/Approval/ApprovalCreate';
import {
  NATIVE_APPROVAL_MODAL_CLASS,
  nativeApprovalModalStyles,
} from '@/components/Approval/ApprovalCreate/styles';
import UnsavedCommentConfirmModal from '@/components/Approval/ApprovalView/UnsavedCommentConfirmModal';
import useApprovalEditMode, {
  EditModeLoading,
} from '@/components/Approval/@hooks/useApprovalEditMode';
import { resolveApprovalDoc } from '@/components/Approval/@hooks/useNextApprovalDoc';
import type { NextApprovalDoc } from '@/components/Approval/@hooks/useNextApprovalDoc';
import { useDialog } from '@/hooks/global-tools';
import type { ApprovalDecisionAction } from '@/hooks/approval/useApprovalDecision';
import { NATIVE_APPROVAL_TYPES, NATIVE_VIEW_APPROVAL_TYPES } from '@/constant';

type ApprovalModalProps = {
  url: string;
  docId: string;
  approvalType?: string;
  dataItemChange?: (id: string, changes: Record<string, any>) => void;
  handleClose: () => void;
  /** 결재(승인/반려/보류) 성공 후 호스트(Content)가 다음 결재 문서 후처리. */
  onDecided?: (docId: string, action: ApprovalDecisionAction) => void;
  /**
   * 레거시 iframe 레이어의 "다음 결재 문서 열기" — 레거시가 이미 확인을 받은 뒤라
   * 호스트는 레이어 없이 바로 문서를 갈아끼우면 됨.
   */
  onOpenNextDoc?: (next: NextApprovalDoc) => void;
};

const ApprovalModal = forwardRef<ModalRef, ApprovalModalProps>((props, ref) => {
  const {
    url,
    docId,
    approvalType,
    dataItemChange,
    handleClose,
    onDecided,
    onOpenNextDoc,
  } = props;
  const { t } = useTranslation();
  const dialog = useDialog();
  const [needCloseMessage, setNeedCloseMessage] = useState(false);
  const commentDirtyRef = useRef<(() => boolean) | null>(null);
  // 편집(ApprovalCreate) 폼의 이탈 경고 판정 — 편집 중 모달 닫기 시 확인.
  const formDirtyRef = useRef<(() => boolean) | null>(null);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);

  const isNativeSupported =
    approvalType && NATIVE_VIEW_APPROVAL_TYPES.includes(Number(approvalType));
  // 편집 폼 preload 는 작성 화이트리스트 기준 — 조회전용 유형은 V2 작성폼이 없음
  const isEditSupported =
    approvalType && NATIVE_APPROVAL_TYPES.includes(Number(approvalType));

  const { isEditMode, setIsEditMode, initialFormState } = useApprovalEditMode(
    isEditSupported ? docId : null,
  );

  // 다른 docId 로 모달 재오픈 시 edit 모드/가드 모달 초기화.
  useEffect(() => {
    setIsEditMode(false);
    setShowUnsavedConfirm(false);
  }, [docId]);

  // 모든 상태 reset (재오픈 시 편집모드 잔존 버그 방지)
  const doClose = () => {
    setShowUnsavedConfirm(false);
    setIsEditMode(false);
    handleClose();
  };

  // 닫기 전 가드: (1) 편집 중이고 폼에 (작성/변경) 내용 있으면 확인 팝업, (2) 작성 중 코멘트 있으면 가드 모달.
  const handleCloseWithReset = () => {
    if (isEditMode && formDirtyRef.current?.()) {
      dialog.show({
        message: t(
          '작성화면을 닫으시겠어요?\n임시저장 되지 않은 내용은 사라집니다.',
        ),
        confirmButtonText: t('나가기'),
        cancelButtonText: t('취소'),
        onConfirm: doClose,
      });
      return;
    }
    if (commentDirtyRef.current?.()) {
      setShowUnsavedConfirm(true);
      return;
    }
    doClose();
  };

  const onMessage = (action: IframeApprovalMessageAction, data: any) => {
    switch (action) {
      case 'MODAL_CLOSE': {
        handleCloseWithReset();
        break;
      }
      case 'APPROVAL_CONFIRM': {
        if ('status' in data && 'docid' in data) {
          dataItemChange?.(data.docid, { status: data.status });
        }
        break;
      }
      case 'NEXT_CONFIRM_DOCUMENT': {
        if ('docid' in data && data.docid) {
          void resolveApprovalDoc(String(data.docid)).then((next) =>
            onOpenNextDoc?.(next),
          );
        }
        break;
      }
      case 'ENABLE_ALERT': {
        setNeedCloseMessage(true);
        break;
      }
      case 'DISABLE_ALERT': {
        setNeedCloseMessage(false);
        break;
      }
    }
  };

  return (
    <>
      {/* V2 편집(작성, ApprovalCreate)일 때만 작성 모달과 같은 확대 너비·full-bleed 적용. 조회는 기본 모달 너비 유지 */}
      {isEditMode && <Global styles={nativeApprovalModalStyles} />}
      <Modal
        ref={ref}
        className={isEditMode ? NATIVE_APPROVAL_MODAL_CLASS : undefined}
        title={'Approval'}
        isFixedHeight
        isExpandable
        availableAutoClose={false}
        noBodyPadding={isEditMode} // V2 편집(작성, ApprovalCreate)일 때만 예외적으로 모달 본문 내 여백 없애기
        handleClose={handleCloseWithReset}
      >
        {isNativeSupported ? (
          // 편집은 display:none 숨김만 — unmount 시 AI 요약 retry 가드 초기화로 over-call 됨
          <>
            <div style={{ display: isEditMode ? 'none' : 'contents' }}>
              <ApprovalView
                documentId={docId}
                apvType={approvalType ? Number(approvalType) : undefined}
                onClose={handleCloseWithReset}
                onStatusChange={(status) => dataItemChange?.(docId, { status })}
                onEdit={() => setIsEditMode(true)}
                onDecided={(action) => onDecided?.(docId, action)}
                commentDirtyRef={commentDirtyRef}
              />
            </div>
            {isEditMode &&
              (initialFormState ? (
                <ApprovalCreate
                  apvType={Number(approvalType)}
                  mode="edit"
                  documentId={docId}
                  initialFormState={initialFormState}
                  onSubmitSuccess={(submittedDocId) => {
                    // handleCloseWithReset 을 부르면 저장에 성공했는데도 '나가기' 확인 팝업이 떠서 편집 모드 해제만 한다.
                    dataItemChange?.(submittedDocId, { status: 'P' });
                    setIsEditMode(false);
                  }}
                  onExit={() => setIsEditMode(false)}
                  exitGuardRef={formDirtyRef}
                />
              ) : (
                <EditModeLoading />
              ))}
          </>
        ) : (
          <IframeApproval
            url={url}
            needCloseMessage={needCloseMessage}
            onMessage={onMessage}
          />
        )}
      </Modal>
      {/* 작성 중 코멘트 확인 모달 */}
      <UnsavedCommentConfirmModal
        open={showUnsavedConfirm}
        onConfirmClose={doClose}
        onKeepEditing={() => setShowUnsavedConfirm(false)}
      />
    </>
  );
});
ApprovalModal.displayName = 'ApprovalModal';

export default ApprovalModal;
